import { usesHostedBilling, isMacAppStore } from '@/lib/appDistribution';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { StyleSheet, ActivityIndicator, View, Pressable, Platform, Linking } from 'react-native';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { accountApi, whopApi } from '@/lib/api';
import { checkIsIapSubscriber, checkIsWhopSubscriber, checkIsValmarSubscriber, checkIsGoogleIapSubscriber, PROCESSOR, hasValidPlusAccess } from '@/lib/purchaseUtils';
import { useAuthStore, useModalStore, useToastStore } from '@/store';
import PricingUpgrade from '@/components/PricingUpgrade';
import { UpgradeIntroduction, UpgradeBenefits } from '@/components/Settings/UpgradePlanPicker';
import {
  BillingGroup,
  BillingRow,
  BillingSection,
  BillingTile,
  BillingValueRow,
  BILLING_ROW_ICON_INSET,
  useBillingPalette,
} from '@/components/Settings/billing/BillingGroup';
import { createReceiptHtml } from '@/lib/purchaseReceipt';
import { cardLastFour, formatCardBrand } from '@/lib/cardDisplay';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { presentCodeRedemptionSheetIOS } from 'react-native-iap';
import AutoTopUpSettings from '@/components/Settings/AutoTopUpSettings';
import moment from 'moment-mini';
import { PLUS_MONTHLY_CREDITS_DISPLAY, PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY } from '@/constants/config';
import { useTranslation } from '@/hooks/useTranslation';
import useRestoreIapPurchases, { canRestoreIapPurchases } from '@/hooks/useRestoreIapPurchases';

const createStyles = (colors, palette) => StyleSheet.create({
  container: {
    flex: 1,
    gap: 20,
    position: 'relative',
    paddingBottom: 8,
  },
  centeredContainer: {
    flex: 1,
    width: '100%',
    minHeight: 240,
    justifyContent: 'center',
    alignItems: 'center',
  },
  hero: {
    borderRadius: 18,
    backgroundColor: palette.isDark ? 'rgba(255,255,255,0.055)' : (colors?.solid_color_primary || '#fff'),
    borderColor: palette.isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.07)',
    ...(Platform.OS === 'web' && !palette.isDark
      ? { boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 12px 32px -12px rgba(0,0,0,0.10)' }
      : null),
  },
  heroHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  heroTitle: {
    flex: 1,
    minWidth: 0,
    gap: 3,
  },
  heroBody: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 20,
    gap: 16,
  },
  heroActions: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 8,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: palette.tileFill,
    flexShrink: 0,
  },
  pillDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  notice: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  noticeBody: {
    flex: 1,
    gap: 4,
  },
  noticeAction: {
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  cardGlyph: {
    width: 42,
    height: 28,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: palette.ink,
  },
  tag: {
    paddingHorizontal: 6,
    paddingVertical: 1,
    borderRadius: 5,
    backgroundColor: palette.tileFill,
  },
  receiptTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  receiptTrailing: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  emptyState: {
    alignItems: 'center',
    gap: 6,
    paddingVertical: 32,
    paddingHorizontal: 24,
  },
  embedded: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  featureGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  featureCell: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  featureBody: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
});

// Features data matching PricingModal
const buildFeatures = (isPlusPremium) => [
  {
    icon: 'infinity',
    title: 'Unlimited',
    description: 'messages with default AI model'
  },
  {
    icon: 'rocket',
    title: 'Supercharged limits',
    description: 'for conversations, custom agents, and collections'
  },
  {
    icon: 'sparkles',
    title: `${isPlusPremium ? PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY : PLUS_MONTHLY_CREDITS_DISPLAY} credits`,
    description: ['to use with', { text: 'other', bold: true }, 'AI models & tools']
  },
  {
    icon: 'bolt',
    title: 'Lightning-fast',
    description: ['responses with', { text: 'zero waiting time', bold: true }]
  }
];

const getProductId = (purchase) => {
  const product = purchase?.product?.id || purchase?.product;
  return typeof product === 'string' ? product : null;
};

const isPremiumPlan = (purchase) => {
  const product = getProductId(purchase);
  return Boolean(product && product.startsWith('plus-premium'));
};

const isAnnualPlan = (purchase) => {
  const product = getProductId(purchase);
  if (product === 'plus-premium-annual') return true;
  if (product === 'plus-premium-monthly') return false;
  if (purchase?.recurring?.schedule === 'yearly') return true;
  if (product === 'plus-annual') return true;
  if (purchase?.price >= 100) return true;
  return false;
};

const isWeeklyPlan = (purchase) => {
  if (purchase?.recurring?.schedule === 'weekly') return true;
  if (purchase?.productId?.includes?.('.weekly')) return true;
  if (purchase?.price && parseFloat(purchase.price) < 10 && parseFloat(purchase.price) >= 5) return true;
  return false;
};

const getPlanTypeLabel = (purchase) => {
  if (isAnnualPlan(purchase)) return 'Annual';
  if (isWeeklyPlan(purchase)) return 'Weekly';
  return 'Monthly';
};

const getBillingPeriod = (purchase) => {
  if (isAnnualPlan(purchase)) return 'year';
  if (isWeeklyPlan(purchase)) return 'week';
  return 'month';
};

// Format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const value = String(dateString);
  const options = {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  };
  // Processor billing dates are date-only values persisted at UTC midnight.
  // Formatting those in a US timezone otherwise displays the previous day.
  if (/^\d{4}-\d{2}-\d{2}(?:T00:00:00(?:\.000)?Z)?$/.test(value)) {
    options.timeZone = 'UTC';
  }
  return new Date(dateString).toLocaleDateString('en-US', options);
};

const formatShortDate = (dateString) => {
  if (!dateString) return '—';
  const date = moment(dateString);
  const currentYear = moment().year();
  const dateYear = date.year();
  
  if (dateYear === currentYear) {
    return date.format('MMM D');
  }
  
  return date.format('MMM D, YYYY');
};

const formatAmount = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(amount);
};

export default function SubscriptionSettings() {
  const { colors } = useTheme();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);
  const userActivePurchase = useAuthStore(state => state.user?.activePurchase);
  const billingIssue = useAuthStore(state => state.user?.activePurchase?.billingIssue);
  const hasWhopSubscription = useAuthStore(state => state.user?.whop?.hasSubscription);
  const plusExpiresAt = useAuthStore(state => state.user?.plusExpiresAt);
  const isPlus = useAuthStore(state => state.user?.flags?.isPlus);
  const isPlusPremium = useAuthStore(state => state.user?.flags?.isPlusPremium);
  const isComped = useAuthStore(state => state.user?.flags?.isComped);
  const setActivePurchase = useAuthStore(state => state.setActivePurchase);
  const fetchUser = useAuthStore(state => state.fetchUser);
  const hasValmarPaymentMethod = useAuthStore(state => state.user?.valmar?.hasPaymentMethod);
  const userValmarCard = useAuthStore(state => state.user?.valmar?.maskedCardData);
  const palette = useBillingPalette();
  const styles = useMemo(() => createStyles(colors, palette), [colors, palette]);
  const { openModal, closeModal } = useModalStore(state => state.actions);
  const { addToast } = useToastStore(state => state.actions);
  const { openUpdatePaymentMethod, isInternational } = useGabPurchase();
  const { t } = useTranslation();
  const { isRestoring: isRestoringPurchases, restorePurchases } = useRestoreIapPurchases();
  const showRestorePurchases = canRestoreIapPurchases();
  // Always verify billing data before rendering it. Persisted purchase state is
  // useful as a fallback, but should not flash stale plan details on first paint.
  const [isLoadingPurchase, setIsLoadingPurchase] = useState(true);

  const isIapSubscriber = useMemo(
    () => checkIsIapSubscriber(userActivePurchase),
    [userActivePurchase],
  );

  const isGoogleIap = useMemo(
    () => checkIsGoogleIapSubscriber(userActivePurchase),
    [userActivePurchase],
  );

  const isWhopSubscriber = useMemo(
    () => checkIsWhopSubscriber(
      userActivePurchase,
      Boolean(isInternational && !isPlus),
      Boolean(hasWhopSubscription),
    ),
    [userActivePurchase, isInternational, isPlus, hasWhopSubscription],
  );

  const hasValidPlus = useMemo(
    () => hasValidPlusAccess({
      isPlus,
      isComped,
      plusExpiresAt,
      purchase: userActivePurchase,
    }),
    [isPlus, isComped, plusExpiresAt, userActivePurchase],
  );

  const plusAccessExpired = Boolean(
    plusExpiresAt && new Date(plusExpiresAt) <= new Date() && !hasValidPlus,
  );

  // Payment history state
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [historyPagination, setHistoryPagination] = useState({});
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [containerWidth, setContainerWidth] = useState(0);
  const handleContainerLayout = useCallback(event => {
    const width = Math.round(event.nativeEvent.layout.width);
    setContainerWidth(prev => (prev === width ? prev : width));
  }, []);
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false);
  const [downloadingReceiptId, setDownloadingReceiptId] = useState(null);

  useEffect(() => {
    // Always refresh subscription data from the backend when the billing tab mounts.
    // This ensures changes made outside the app (e.g. WHOP cancel/re-subscribe) are reflected.
    fetchSubscription();
    // Also refresh the full user record so `state.user.plusExpiresAt` reflects
    // any backend-side changes (admin manual extensions, Whop renewals, etc.).
    // Without this, the "Next Billing Date" can be stuck on the cached value
    // from when the user logged in, even though the database is correct.
    fetchUser?.()?.catch((err) => console.error('Failed to refresh user on billing tab mount', err));
  }, []);

  useEffect(() => {
    // Fetch purchase history on mount
    fetchPurchaseHistory(1);
  }, []);

  const fetchSubscription = async () => {
    try {
      setIsLoadingPurchase(true);
      const purchase = await accountApi.getActivePurchase();
      // A valid cached purchase is better than the API's null fallback for a
      // user who still has current Plus access. Do not keep a stale row when
      // Plus has already expired.
      if (purchase || !hasValidPlus) {
        setActivePurchase(purchase);
      }
      setIsLoadingPurchase(false);
    } catch (err) {
      console.error('Error fetching subscription:', err);
      addToast(t('settings.subscription.fetchFailed'), 'error');
    } finally {
      setIsLoadingPurchase(false);
    }
  };

  const fetchPurchaseHistory = async (page = 1) => {
    try {
      if (page === 1) {
        setIsLoadingHistory(true);
      } else {
        setIsLoadingMoreHistory(true);
      }
      
      const { purchases, pagination } = await accountApi.getPurchaseHistory(page);
      
      if (page === 1) {
        setPurchaseHistory(purchases);
      } else {
        setPurchaseHistory(prev => [...prev, ...purchases]);
      }
      setHistoryPagination(pagination);
    } catch (err) {
      console.error('Error fetching purchase history:', err);
      // Don't show toast for empty history
      if (err.status !== 404) {
        addToast(t('settings.subscription.historyFetchFailed'), 'error');
      }
    } finally {
      setIsLoadingHistory(false);
      setIsLoadingMoreHistory(false);
    }
  };

  const handleLoadMoreHistory = useCallback(async () => {
    if (isLoadingMoreHistory || !historyPagination?.hasMore) return;
    const nextPage = (historyPagination?.current || historyPagination?.page || 1) + 1;
    await fetchPurchaseHistory(nextPage);
  }, [isLoadingMoreHistory, historyPagination]);

  const handleDownloadReceipt = useCallback(async (purchaseId, purchaseData = {}) => {
    try {
      setDownloadingReceiptId(purchaseId);
      
      // Fetch receipt from API (supports both subscription and credit purchases)
      const receipt = await accountApi.getReceipt(purchaseId);
      
      if (receipt) {
        if (Platform.OS === 'web') {
          const receiptHtml = createReceiptHtml(receipt, purchaseId);

          const blob = new Blob([receiptHtml], { type: 'text/html' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `gab-ai-receipt-${receipt.transactionId || receipt.id || purchaseId}.html`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          // For native, use expo-file-system and expo-sharing
          try {
            // Generate a simpler text-based receipt for native
            const isCreditPurchase = receipt.type === 'credits';
            const productName = isCreditPurchase 
              ? `${receipt.product}${receipt.isAutoTopup ? ' (Auto Top-Up)' : ''}`
              : (Platform.OS === 'macos'
                ? (receipt.productName || (receipt.product?.includes('premium') ? 'Gab AI Plus Premium' : 'Gab AI Plus'))
                : (receipt.productName || receipt.product || 'Gab AI Plus'));
            // The receipt API returns price and userEmail for every purchase type.
            const receiptAmount = formatAmount(receipt.price, receipt.currency);
            const receiptEmail = receipt.userEmail;
            const formattedDate = receipt.paymentReceivedDate || receipt.createdAt 
              ? new Date(receipt.paymentReceivedDate || receipt.createdAt).toLocaleDateString('en-US', { 
                  year: 'numeric', month: 'long', day: 'numeric' 
                })
              : 'N/A';
            
            const receiptText = `
GAB AI RECEIPT
==============

Product: ${productName}
Date: ${formattedDate}
Status: ${receipt.status || 'Completed'}
Amount: ${receiptAmount}

Transaction ID: ${receipt.transactionId || receipt.id || purchaseId}
${receiptEmail ? `Email: ${receiptEmail}` : ''}
${receipt.paymentMethod ? `Payment Method: ${receipt.paymentMethod}` : ''}

Thank you for your purchase!
Visit us at gab.ai

This receipt was generated by Gab AI.
            `.trim();
            
            const filename = `gab-ai-receipt-${receipt.transactionId || receipt.id || purchaseId}.txt`;
            const fileUri = `${FileSystem.documentDirectory}${filename}`;
            
            await FileSystem.writeAsStringAsync(fileUri, receiptText, {
              encoding: FileSystem.EncodingType.UTF8,
            });
            
            const canShare = await Sharing.isAvailableAsync();
            if (canShare) {
              await Sharing.shareAsync(fileUri, {
                mimeType: 'text/plain',
                dialogTitle: 'Save Receipt',
              });
            } else {
              addToast(t('settings.subscription.sharingNotAvailable'), 'error');
            }
          } catch (nativeErr) {
            console.error('Error saving receipt on native:', nativeErr);
            addToast(t('settings.subscription.failedToSaveReceipt'), 'error');
          }
        }
      } else {
        addToast(t('settings.subscription.receiptNotAvailable'), 'error');
      }
    } catch (err) {
      console.error('Error downloading receipt:', err);
      addToast(t('settings.subscription.failedToDownloadReceipt'), 'error');
    } finally {
      setDownloadingReceiptId(null);
    }
  }, [addToast]);

  const applyCanceledStateLocally = (result) => {
    const accessEndDate = result?.accessUntil
      || result?.expiresAt
      || result?.recurring?.endDate
      || userActivePurchase?.recurring?.endDate
      || plusExpiresAt;

    const basePurchase = userActivePurchase || {
      id: result?.purchaseId,
      status: 'active',
      recurring: { enabled: true },
    };

    setActivePurchase({
      ...basePurchase,
      canceledDate: result?.canceledDate || new Date(),
      recurring: {
        ...(basePurchase.recurring || {}),
        enabled: false,
        endDate: accessEndDate,
      },
    });
  };

  const handleCancelWhopSubscription = useCallback(() => {
    openModal('ConfirmationModal', {
      title: t('modals.cancelPurchase.title'),
      message: t('modals.cancelPurchase.warningMessage'),
      confirmText: t('modals.cancelPurchase.yesCancel'),
      cancelText: t('modals.cancelPurchase.neverMind'),
      isDanger: true,
      onConfirm: async () => {
        try {
          setIsLoading(true);
          const result = await whopApi.cancelWhopSubscription();
          applyCanceledStateLocally(result);
          addToast(t('settings.subscription.canceledSuccess'), 'success');
          closeModal();
        } catch (error) {
          console.error('Error canceling Whop subscription:', error);
          addToast(error.message || t('modals.cancelPurchase.failedToCancel'), 'error');
        } finally {
          setIsLoading(false);
        }
      },
    });
  }, [openModal, closeModal, addToast, t, userActivePurchase, plusExpiresAt, setActivePurchase]);

  // The existing password-confirmation cancel modal. Extracted so the
  // cancellation-save flow can chain to it when the user opts to cancel anyway.
  const openCancelPurchaseModal = useCallback(() => {
    openModal('CancelPurchaseModal', {
      onConfirm: async (password, reason) => {
        try {
          setIsLoading(true);
          const result = await accountApi.cancelPlan(password, reason);

          // Get the access end date from various possible API response fields
          const accessEndDate = result.accessUntil || result.expiresAt || result.recurring?.endDate || userActivePurchase?.recurring?.endDate || plusExpiresAt;

          // activePurchase can be null for legacy Plus users whose Purchase row
          // hasn't loaded or doesn't match active-purchase query criteria.
          const basePurchase = userActivePurchase || {
            id: result.purchaseId,
            status: 'active',
            recurring: { enabled: true },
          };

          setActivePurchase({
            ...basePurchase,
            canceledDate: result.canceledDate,
            recurring: {
              ...(basePurchase.recurring || {}),
              enabled: false,
              endDate: accessEndDate,
            },
          });

          addToast(t('settings.subscription.canceledSuccess'), 'success');
          closeModal();
        } catch (error) {
          const partial = error?.data;
          if (partial?.code === 'SUBSCRIPTION_CANCELLATION_PARTIAL'
            && userActivePurchase?.id
            && partial.canceledPurchaseIds?.includes(userActivePurchase.id)) {
            setActivePurchase({
              ...userActivePurchase,
              canceledDate: partial.canceledDate,
              recurring: {
                ...(userActivePurchase.recurring || {}),
                enabled: false,
                endDate: partial.accessUntil || userActivePurchase.recurring?.endDate || plusExpiresAt,
              },
            });
            error.message = 'Your current plan was canceled. Another subscription could not be confirmed canceled and may still renew. Please retry to cancel the remaining subscriptions or contact support.';
          }
          throw error; // Let the modal handle the error display
        } finally {
          setIsLoading(false);
        }
      },
      onClose: () => {
        closeModal();
        openModal('SettingsModalWeb', { tab: 'subscription' });
      },
    });
  }, [openModal, closeModal, setActivePurchase, userActivePurchase, plusExpiresAt, addToast, t]);

  const handleCancelSubscription = async () => {
    // Whop subscribers cancel via our backend (Whop's hosted portal sometimes
    // only exposes Extend/Upgrade and hides the cancel action).
    if (isWhopSubscriber) {
      handleCancelWhopSubscription();
      return;
    }

    // Web + Valmar subscribers see the cancellation-save flow first (a retention
    // offer). Every other path (native IAP, legacy processors) and any decline
    // falls through to the plain password-confirmation cancel modal.
    // See docs/PLUS_CANCELLATION_SAVE.md Part II/§30.
    if (Platform.OS === 'web' && isValmarSubscriber) {
      openModal('CancellationSaveModal', {
        onAccepted: () => {
          addToast(t('modals.cancellationSave.offerApplied'), 'success');
          closeModal();
          openModal('SettingsModalWeb', { tab: 'subscription' });
        },
        onCancelAnyway: () => {
          // Replace the save modal with the real cancel modal.
          closeModal();
          openCancelPurchaseModal();
        },
        onClose: () => {
          closeModal();
          openModal('SettingsModalWeb', { tab: 'subscription' });
        },
      });
      return;
    }

    openCancelPurchaseModal();
  };

  // Handle manage subscription for international Whop users
  const [isLoadingManage, setIsLoadingManage] = useState(false);
  
  const handleManageSubscription = useCallback(async () => {
    if (!usesHostedBilling()) return;
    try {
      setIsLoadingManage(true);
      const response = await whopApi.getBillingPortalUrl();
      const portalUrl = response.portalUrl || response.manageUrl;
      
      if (!portalUrl) {
        throw new Error('Failed to get Whop billing portal URL');
      }
      
      // Open Whop billing portal
      if (Platform.OS === 'web') {
        window.open(portalUrl, '_blank');
      } else {
        await Linking.openURL(portalUrl);
      }
    } catch (error) {
      console.error('Error opening Whop billing portal:', error);
      addToast(error.message || t('settings.subscription.failedToOpenPortal'), 'error');
    } finally {
      setIsLoadingManage(false);
    }
  }, [addToast]);

  const handleUpdatePaymentMethod = useCallback(async () => {
    if (!usesHostedBilling()) return;
    try {
      setIsUpdatingPayment(true);
      
      // Whop subscribers update payment methods via Whop billing portal
      if (isWhopSubscriber) {
        const response = await whopApi.getBillingPortalUrl();
        const portalUrl = response.portalUrl || response.manageUrl;
        
        if (!portalUrl) {
          throw new Error('Failed to get Whop billing portal URL');
        }
        
        // Open Whop billing portal
        if (Platform.OS === 'web') {
          window.open(portalUrl, '_blank');
        } else {
          await Linking.openURL(portalUrl);
        }
        return;
      }
      
      // Web and macOS use the existing hosted Valmar payment-method form.
      if (!usesHostedBilling()) {
        addToast(t('settings.subscription.manageWebPayment'), 'info');
        return;
      }
      
      const response = await accountApi.initiatePaymentMethodUpdate();
      
      if (!response.updateUrl) {
        throw new Error('Failed to get update URL');
      }
      
      // Open the update payment iframe with the URL from the API
      const supportedPlanLengths = ['monthly', 'yearly', 'premium-monthly', 'premium-yearly'];
      await openUpdatePaymentMethod(response.updateUrl, {
        planLength: supportedPlanLengths.includes(response.planLength)
          ? response.planLength
          : 'monthly',
      });
    } catch (error) {
      console.error('Error updating payment method:', error);
      if (error.message !== 'User closed checkout' && 
          error.message !== 'Backdrop close' && 
          error.message !== 'User closed update payment') {
        addToast(error.message || t('settings.subscription.failedToUpdatePayment'), 'error');
      }
    } finally {
      setIsUpdatingPayment(false);
    }
  }, [openUpdatePaymentMethod, addToast, isWhopSubscriber]);

  const renderReceiptRow = useCallback((row, index) => {
    const isCreditPurchase = row.type === 'credits' || row.creditPackId || row.credits;
    const credits = row.credits || row.creditPack?.credits;
    const title = isCreditPurchase
      ? (credits ? `${credits.toLocaleString()} Credits` : t('settings.subscription.creditPack'))
      : `${row.product?.name || (isPremiumPlan(row) ? 'Gab AI Plus Premium' : 'Gab AI Plus')} ${getPlanTypeLabel(row)}`;
    const status = String(row.status || 'completed');
    const isPaid = ['active', 'completed', 'success'].includes(status);
    const isFailed = ['canceled', 'failed', 'refunded'].includes(status);
    const statusLabel = isPaid
      ? t('settings.subscription.paid')
      : status.charAt(0).toUpperCase() + status.slice(1);
    const isDownloading = downloadingReceiptId === row.id;

    return (
      <BillingRow
        key={`purchase-${index}-${row.id}`}
        testID={`receipt-${row.id}`}
        icon={isCreditPurchase ? 'coins' : 'sparkles'}
        title={(
          <View style={styles.receiptTitle}>
            <Text size="n" weight="medium" color="primary" numberOfLines={1} style={{ flexShrink: 1 }}>
              {title}
            </Text>
            {row.isAutoTopup && (
              <View style={styles.tag}>
                <Text size="xxs" weight="medium" color="secondary">{t('settings.subscription.autoTopUp')}</Text>
              </View>
            )}
          </View>
        )}
        subtitle={(
          <Text size="s" color="secondary">
            {formatShortDate(row.createdAt || row.date)}
            {' · '}
            <Text size="s" color={isFailed ? 'danger' : 'secondary'}>{statusLabel}</Text>
          </Text>
        )}
        trailing={(
          <View style={styles.receiptTrailing}>
            <Text size="n" weight="medium" color="primary">{formatAmount(row.price || row.amount, row.currency)}</Text>
            <Button
              variant="secondary"
              size="small"
              text={Platform.OS === 'ios' || Platform.OS === 'android' ? 'Invoice' : 'Download invoice'}
              prependIcon="download"
              onPress={() => handleDownloadReceipt(row.id, row)}
              isLoading={isDownloading}
              disabled={isDownloading}
            />
          </View>
        )}
      />
    );
  }, [downloadingReceiptId, handleDownloadReceipt, styles, colors, t]);

  // Present Apple's native offer code redemption sheet (iOS only)
  const [isRedeemingCode, setIsRedeemingCode] = useState(false);
  const handleRedeemCode = useCallback(async () => {
    if (Platform.OS !== 'ios') return;
    try {
      setIsRedeemingCode(true);
      await presentCodeRedemptionSheetIOS();
    } catch (error) {
      console.error('Error presenting code redemption sheet:', error);
    } finally {
      setIsRedeemingCode(false);
    }
  }, []);

  // For Plus users whose Purchase row is missing or drifted off the
  // active-purchase response (legacy Paddle, admin/manual Plus, or stale
  // processor status),
  // synthesize a minimal purchase object so the active-subscription
  // view renders instead of the "No Active Subscription" empty state
  // and the upgrade-from-empty-state flow that rejects Plus users.
  // Never synthesize an Active card after plusExpiresAt — that produced
  // "Active" with Started/Expires N/A for lapsed Paddle annuals.
  const purchase = userActivePurchase || (hasValidPlus ? {
    status: 'active',
    // The access flags identify the tier, not its billing cadence. Do not
    // invent an annual SKU when the Purchase row is unavailable.
    product: null,
    price: null,
    currency: 'USD',
    paymentReceivedDate: null,
    canceledDate: null,
    // Prefer Whop when we know the user has an active Whop membership, then
    // Valmar (card on file). This keeps payment management visible even when
    // the Purchase row hasn't loaded or drifted off the active-purchase response.
    processor: isWhopSubscriber
      ? PROCESSOR.WHOP
      : (hasValmarPaymentMethod ? PROCESSOR.VALMAR : null),
    // A saved Valmar card does not establish recurring billing.
    recurring: { enabled: Boolean(isWhopSubscriber) },
  } : null);

  const handleManageStoreSubscription = useCallback(async () => {
    const url = purchase?.processor === PROCESSOR.GOOGLE
      ? 'https://play.google.com/store/account/subscriptions'
      : purchase?.processor === PROCESSOR.APPLE
        ? 'https://apps.apple.com/account/subscriptions' : null;
    if (!url) return;
    try {
      await Linking.openURL(url);
    } catch (error) {
      useToastStore.getState().actions.addToast(error?.message || 'Unable to open subscription management.', 'error');
    }
  }, [purchase?.processor]);

  if (isLoadingPurchase) {
    return (
      <View style={styles.centeredContainer}>
        <ActivityIndicator size="large" color={colors.text_color_primary} />
      </View>
    );
  }

  const isValmarSubscriber = checkIsValmarSubscriber(purchase, { hasValmarPaymentMethod, isPlus: hasValidPlus });
  const isActive = hasValidPlus && purchase?.status === 'active';
  const isCanceled = Boolean(purchase?.canceledDate);
  const hasActiveSubscription = isActive && purchase;
  const hasProcessorSubscription = Boolean(
    purchase?.processorSubscriptionId ||
    purchase?.recurring?.subscriptionId
  );
  const isDirectlyCancelableProcessor = [
    PROCESSOR.VALMAR,
    PROCESSOR.AUTHNET,
    PROCESSOR.PADDLE,
  ].includes(purchase?.processor);
  const hasCancelableSubscription = Boolean(
    hasActiveSubscription &&
    !isCanceled &&
    (purchase?.processor !== PROCESSOR.VALMAR ||
      (purchase?.recurring?.enabled === true && hasProcessorSubscription)) &&
    (
      isIapSubscriber ||
      isWhopSubscriber ||
      isDirectlyCancelableProcessor ||
      (isPlus && hasValmarPaymentMethod && !isIapSubscriber && !isWhopSubscriber)
    )
  );
  const isRecurring = !isCanceled && (purchase?.processor === PROCESSOR.VALMAR
    ? purchase?.recurring?.enabled === true && hasProcessorSubscription
    : purchase?.recurring?.enabled ||
      (purchase?.recurring?.enabled !== false && hasProcessorSubscription) ||
      (isActive && purchase?.recurring?.enabled !== false && purchase?.processor === PROCESSOR.WHOP));
  const hasKnownPlanCadence = Boolean(
    getProductId(purchase)
    || purchase?.recurring?.schedule
    || purchase?.price,
  );

  // Get the subscription start date from various possible fields
  const getStartDate = () => {
    // Try recurring.startDate first
    if (purchase?.recurring?.startDate) {
      return formatDate(purchase.recurring.startDate);
    }
    // Fall back to paymentReceivedDate (when payment was first received)
    if (purchase?.paymentReceivedDate) {
      return formatDate(purchase.paymentReceivedDate);
    }
    // Fall back to createdAt (when purchase record was created)
    if (purchase?.createdAt) {
      return formatDate(purchase.createdAt);
    }
    return 'N/A';
  };

  // Get the raw start date for calculations
  const getRawStartDate = () => {
    return purchase?.recurring?.startDate || purchase?.paymentReceivedDate || purchase?.createdAt;
  };

  // For canceled subscriptions, calculate access end date if not provided
  // Monthly subscriptions renew on the same date each month from start date
  const getAccessEndDate = () => {
    // Non-renewing access has a fixed expiration. Never advance a purchase
    // anniversary into the future as though another cycle had been paid.
    if (!isRecurring && !isCanceled) {
      const expiresAt = [plusExpiresAt, purchase?.recurring?.endDate]
        .find(date => date && Number.isFinite(new Date(date).getTime()));
      return expiresAt ? formatDate(expiresAt) : 'N/A';
    }
    // Use the backend-provided plusExpiresAt for active subscriptions when it's
    // still in the future — it correctly accounts for extended/carried-over days.
    // If plusExpiresAt is stale (renewal day before webhook fires), fall through
    // to purchase-based dates so the UI doesn't show a past date while "Active".
    if (plusExpiresAt && !isCanceled && new Date(plusExpiresAt) > new Date()) {
      return formatDate(plusExpiresAt);
    }

    // Fall back to the explicit end date from recurring data
    if (purchase?.recurring?.endDate) {
      return formatDate(purchase.recurring.endDate);
    }
    
    const rawStartDate = getRawStartDate();
    const isAnnual = isAnnualPlan(purchase);
    const isWeekly = isWeeklyPlan(purchase);
    
    const renewalUnit = isAnnual ? 'year' : isWeekly ? 'week' : 'month';

    // For canceled subscriptions without end date, try to calculate from start date
    if (isCanceled && rawStartDate) {
      const startMoment = moment(rawStartDate);
      const now = moment();
      let nextDate = startMoment.clone();
      while (nextDate.isBefore(now)) {
        nextDate.add(1, renewalUnit);
      }
      return formatDate(nextDate.toISOString());
    }
    
    // For active subscriptions without explicit end date, calculate from start date
    if (isAnnual && rawStartDate) {
      const startMoment = moment(rawStartDate);
      const now = moment();
      let nextDate = startMoment.clone();
      while (nextDate.isBefore(now) || nextDate.isSame(now, 'day')) {
        nextDate.add(1, 'year');
      }
      return formatDate(nextDate.toISOString());
    }
    
    if (rawStartDate) {
      const startMoment = moment(rawStartDate);
      const now = moment();
      let nextDate = startMoment.clone();
      while (nextDate.isBefore(now) || nextDate.isSame(now, 'day')) {
        nextDate.add(1, renewalUnit);
      }
      return formatDate(nextDate.toISOString());
    }
    
    return 'N/A';
  };

  const getNextRenewalDate = () => {
    // Whop's live charge date is independent of carried-over account access.
    if (purchase?.processor === PROCESSOR.WHOP && purchase?.recurring?.nextBilledAt
      && Number.isFinite(new Date(purchase.recurring.nextBilledAt).getTime())) {
      return formatDate(purchase.recurring.nextBilledAt);
    }
    // The account expiration includes time carried over by an extension,
    // even when an older Purchase still has its original renewal dates.
    // Apple/Google billing dates remain store-controlled: an account access
    // grant does not move the next charge in those stores.
    if (!isIapSubscriber && !isCanceled && plusExpiresAt && new Date(plusExpiresAt) > new Date()) {
      return formatDate(plusExpiresAt);
    }

    const storedNext = [purchase?.recurring?.nextBilledAt, purchase?.recurring?.endDate]
      .find(date => date && new Date(date) > new Date());
    if (storedNext) {
      return formatDate(storedNext);
    }

    const rawStartDate = getRawStartDate();
    if (!rawStartDate) return 'N/A';

    const renewalUnit = isAnnualPlan(purchase)
      ? 'year'
      : isWeeklyPlan(purchase)
        ? 'week'
        : 'month';
    const nextDate = moment(rawStartDate);
    const now = moment();
    while (nextDate.isBefore(now) || nextDate.isSame(now, 'day')) {
      nextDate.add(1, renewalUnit);
    }
    return formatDate(nextDate.toISOString());
  };
  
  const nextBillDate = isRecurring && !isCanceled ? getNextRenewalDate() : getAccessEndDate();
  const startDate = getStartDate();
  const expiredOnDate = plusAccessExpired ? formatDate(plusExpiresAt) : null;

  const planName = isPlusPremium ? 'Gab AI Plus Premium' : (purchase?.product?.name || 'Gab AI Plus');
  const whopRenewalPrice = purchase?.processor === PROCESSOR.WHOP && purchase?.renewalPriceDescription;
  const priceSummary = whopRenewalPrice || (purchase?.price
    ? `${formatAmount(purchase.price, purchase.currency)} per ${getBillingPeriod(purchase)}`
    : null);
  const nextBillSummary = nextBillDate !== 'N/A'
    ? `Next bill on ${nextBillDate}${whopRenewalPrice ? ` • ${whopRenewalPrice}` : purchase?.price ? ` • ${formatAmount(purchase.price, purchase.currency)}` : ''}`
    : null;
  const storeName = isGoogleIap ? 'Google Play' : 'App Store';
  const isNativeMobile = Platform.OS === 'ios' || Platform.OS === 'android';
  const twoColumn = containerWidth >= 560;

  const renderStatusPill = (label, dotColor) => (
    <View style={styles.pill}>
      <View style={[styles.pillDot, { backgroundColor: dotColor }]} />
      <Text size="xs" weight="semibold" color="primary">{label}</Text>
    </View>
  );

  const renderHeroHeader = ({ title, subtitle, pill, emphasized }) => (
    <View style={styles.heroHeader}>
      <BillingTile
        icon="sparkles"
        size={40}
        radius={10}
        iconSize={17}
        solid
        color={emphasized ? palette.inkInverse : palette.ink}
        backgroundColor={emphasized ? palette.ink : undefined}
      />
      <View style={styles.heroTitle}>
        <Text size="l" weight="bold" color="primary">{title}</Text>
        {subtitle ? <Text size="s" color="secondary">{subtitle}</Text> : null}
      </View>
      {pill}
    </View>
  );

  const renderPlanCard = () => {
    if (!hasActiveSubscription) {
      return (
        <View style={{ gap: 24 }}>
          {plusAccessExpired && <BillingGroup>
            {renderHeroHeader({
              title: planName,
              subtitle: t('settings.subscription.subscriptionExpiredMsg'),
              pill: renderStatusPill(t('settings.subscription.expired'), '#8E8E93'),
            })}
            {startDate !== 'N/A' && <BillingValueRow key="started" label={t('settings.subscription.started')} value={startDate} />}
            <BillingValueRow key="expired" label={t('settings.subscription.expired')} value={expiredOnDate} />
          </BillingGroup>}
          {isNativeMobile ? (
            <View key="native-cta" style={{ gap: 24 }}>
              <UpgradeIntroduction />
              <Button
                size="large"
                text={plusAccessExpired ? t('settings.subscription.resubscribe') : t('settings.subscription.viewPlans')}
                textWeight="bold"
                onPress={() => router.push('/paywall')}
                variant="primary"
              />
              <UpgradeBenefits />
            </View>
          ) : (
            <PricingUpgrade
              presentation="settings"
              withPadding={false}
              withScroll={false}
              onContinue={hasValidPlus ? () => openModal('SubscriptionExtendModal') : undefined}
            />
          )}
        </View>
      );
    }

    const dateLabel = isCanceled
      ? t('settings.subscription.accessUntil')
      : isRecurring
        ? t('settings.subscription.nextBillingDate')
        : t('settings.subscription.expires');

    return (
      <BillingSection
        footer={isIapSubscriber && Platform.OS === 'web'
          ? `This subscription is managed through ${isGoogleIap ? 'Google Play' : 'the App Store'}. Use the button above, sign in with the store account used for your purchase, and select Gab AI to manage or cancel your subscription.`
          : null}
      >
        <BillingGroup style={styles.hero}>
          {renderHeroHeader({
            title: `${planName}${hasKnownPlanCadence ? ` ${getPlanTypeLabel(purchase)}` : ''}`,
            subtitle: priceSummary,
            emphasized: true,
            pill: isCanceled
              ? renderStatusPill(t('settings.subscription.canceled'), '#FF9F0A')
              : renderStatusPill(t('settings.subscription.active'), '#34C759'),
          })}
          <BillingValueRow key="started" label={t('settings.subscription.started')} value={startDate} />
          <BillingValueRow
            key="date"
            label={dateLabel}
            value={nextBillDate}
            valueColor={isCanceled ? 'danger' : 'primary'}
            valueWeight={isCanceled ? 'semibold' : 'medium'}
          />
          {isActive && !isIapSubscriber && usesHostedBilling() && (
            <View key="extend" style={styles.heroActions}>
              <Button
                variant={isCanceled ? 'plus' : 'secondary'}
                text={isCanceled ? t('settings.subscription.resubscribe') : t('settings.subscription.extendOrUpgrade')}
                onPress={() => openModal('SubscriptionExtendModal')}
                prependIcon={isCanceled ? 'bolt' : 'rotate-right'}
                isFullWidth
              />
            </View>
          )}
          {isActive && isIapSubscriber && (
            <View key="store" style={styles.heroActions}>
              <Button
                variant="secondary"
                text={Platform.OS === 'web'
                  ? `Manage / cancel in ${storeName}`
                  : (purchase?.processor === PROCESSOR.GOOGLE ? t('settings.subscription.manageInGooglePlay') : t('settings.subscription.manageInAppStore'))}
                onPress={handleManageStoreSubscription}
                prependIcon="arrow-up-right-from-square"
                isFullWidth
              />
            </View>
          )}
          {isActive && !isIapSubscriber && hasCancelableSubscription && (
            <BillingRow
              key="cancel"
              destructive
              title={t('settings.subscription.cancelSubscription')}
              onPress={handleCancelSubscription}
              disabled={isLoading}
            />
          )}
        </BillingGroup>
      </BillingSection>
    );
  };

  // Apple reports billing failures during grace and billing-retry periods.
  const renderBillingIssue = () => {
    if (!billingIssue?.active || !hasActiveSubscription) return null;

    const graceDateStr = billingIssue.gracePeriodExpiresAt;
    const expiresLabel = graceDateStr
      ? formatDate(graceDateStr)
      : billingIssue.retryPeriodExpiresAt
        ? formatDate(billingIssue.retryPeriodExpiresAt)
        : null;

    let action = null;
    if (isIapSubscriber && (isNativeMobile || Platform.OS === 'macos')) {
      action = (
        <Button
          variant="danger"
          size="small"
          text={purchase?.processor === PROCESSOR.GOOGLE ? 'Update Payment in Google Play' : 'Update Payment in App Store'}
          onPress={handleManageStoreSubscription}
          prependIcon="arrow-up-right-from-square"
        />
      );
    } else if (isIapSubscriber && Platform.OS === 'web') {
      action = (
        <Text size="s" color="secondary">
          Open the Gab AI app on your mobile device to update your payment method.
        </Text>
      );
    } else if (isWhopSubscriber && usesHostedBilling()) {
      action = (
        <Button
          variant="danger"
          size="small"
          text="Update Payment Method"
          isLoading={isLoadingManage}
          onPress={handleManageSubscription}
          prependIcon="arrow-up-right-from-square"
        />
      );
    } else if (usesHostedBilling()) {
      action = (
        <Button
          variant="danger"
          size="small"
          text="Update Payment Method"
          isLoading={isUpdatingPayment}
          onPress={handleUpdatePaymentMethod}
        />
      );
    }

    return (
      <BillingGroup>
        <View style={styles.notice}>
          <BillingTile
            icon="exclamation-triangle"
            solid
            color={palette.destructive}
            backgroundColor={palette.isDark ? 'rgba(255,69,58,0.18)' : 'rgba(255,59,48,0.10)'}
          />
          <View style={styles.noticeBody}>
            <Text size="n" weight="semibold" color="primary">Billing Issue</Text>
            <Text size="s" color="secondary">
              {graceDateStr
                ? `There was a problem with your payment. Your subscription will remain active until ${expiresLabel || 'the grace period ends'}. Please update your payment method to avoid losing access.`
                : 'There was a problem renewing your subscription. Please update your payment method to continue your subscription.'}
            </Text>
            {action && <View style={styles.noticeAction}>{action}</View>}
          </View>
        </View>
      </BillingGroup>
    );
  };

  // Saved cards can be managed independently of subscription access.
  const renderPaymentMethod = () => {
    if (isMacAppStore() && !isIapSubscriber) return null;
    if (!hasValmarPaymentMethod && (!hasActiveSubscription || (!isRecurring && !isValmarSubscriber))) return null;

    let row;
    let footer = null;

    if (isIapSubscriber) {
      row = (
        <BillingRow
          icon="mobile-screen-button"
          title={`Managed via ${storeName}`}
          subtitle={isNativeMobile || Platform.OS === 'macos'
            ? `Update your payment method in your ${storeName} settings`
            : `Your payment method is managed through ${storeName}. Use the store-management button above to open your subscription settings.`}
        />
      );
      footer = !isCanceled ? nextBillSummary : null;
    } else if (isWhopSubscriber && usesHostedBilling()) {
      row = (
        <BillingRow
          icon="credit-card"
          title="Managed via Whop"
          subtitle="Update your payment method in the Whop billing portal"
          trailing={(
            <Button
              variant="secondary"
              size="small"
              text={t('settings.subscription.update')}
              isLoading={isLoadingManage}
              disabled={isLoadingManage}
              onPress={handleManageSubscription}
              appendIcon="arrow-up-right-from-square"
            />
          )}
        />
      );
      footer = !isCanceled ? nextBillSummary : null;
    } else {
      if (!isValmarSubscriber && !hasValmarPaymentMethod) return null;
      const cardType = formatCardBrand(
        purchase?.maskedCardData?.cardType || userValmarCard?.cardType || purchase?.paymentType,
      );
      const cardLast4 = cardLastFour(
        purchase?.maskedCardData?.maskedCardNumber
          || purchase?.maskedCardData?.maskedNumber
          || userValmarCard?.maskedNumber,
      );
      const cardExpiry = purchase?.maskedCardData?.expirationDate || userValmarCard?.expirationDate;
      row = (
        <BillingRow
          leading={(
            <View style={styles.cardGlyph}>
              <Icon name="credit-card" size={14} color={palette.inkInverse} />
            </View>
          )}
          title={cardLast4 ? `${cardType} ending in ${cardLast4}` : cardType}
          subtitle={cardExpiry ? `Expires ${cardExpiry}` : null}
          trailing={(
            <Button
              variant="secondary"
              size="small"
              text={t('settings.subscription.update')}
              isLoading={isUpdatingPayment}
              disabled={isUpdatingPayment}
              onPress={handleUpdatePaymentMethod}
            />
          )}
        />
      );
      footer = isRecurring ? nextBillSummary : null;
    }

    return (
      <BillingSection title={t('settings.subscription.paymentMethod')} footer={footer}>
        <BillingGroup>{row}</BillingGroup>
      </BillingSection>
    );
  };

  const renderPlanFeatures = () => {
    if (!hasActiveSubscription) return null;
    return (
      <BillingSection title={t('settings.subscription.planFeatures')}>
        <BillingGroup>
          <View style={styles.featureGrid}>
            {buildFeatures(isPlusPremium).map(feature => (
              <View key={feature.icon} style={[styles.featureCell, { width: twoColumn ? '50%' : '100%' }]}>
                <BillingTile icon={feature.icon} size={26} radius={7} iconSize={12} />
                <View style={styles.featureBody}>
                  <Text size="s" weight="semibold" color="primary">{feature.title}</Text>
                  <Text size="s" color="secondary">
                    {(Array.isArray(feature.description) ? feature.description : [feature.description]).map((part, i) => (
                      typeof part === 'object'
                        ? <Text key={`${feature.icon}-${i}`} size="s" weight="semibold" color="primary">{`${part.text} `}</Text>
                        : <Text key={`${feature.icon}-${i}`} size="s" color="secondary">{`${part} `}</Text>
                    ))}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </BillingGroup>
      </BillingSection>
    );
  };

  const renderStoreActions = () => {
    if (Platform.OS !== 'ios' && !showRestorePurchases) return null;
    return (
      <BillingSection title="Purchases">
        <BillingGroup inset={BILLING_ROW_ICON_INSET}>
          {Platform.OS === 'ios' && (
            <BillingRow
              key="redeem"
              icon="ticket"
              title={isRedeemingCode ? t('settings.subscription.opening') : t('settings.subscription.redeemOfferCode')}
              onPress={handleRedeemCode}
              disabled={isRedeemingCode}
              chevron
            />
          )}
          {showRestorePurchases && (
            <BillingRow
              key="restore"
              icon="rotate-right"
              title={isRestoringPurchases ? t('settingsMobile.restoringPurchases') : t('settings.subscription.restorePurchases')}
              onPress={restorePurchases}
              isLoading={isRestoringPurchases}
              chevron
            />
          )}
        </BillingGroup>
      </BillingSection>
    );
  };

  const renderReceipts = () => {
    if (!hasActiveSubscription && purchaseHistory.length === 0) return null;
    let content;
    if (isLoadingHistory) {
      content = (
        <View style={styles.emptyState}>
          <ActivityIndicator size="small" color={colors?.text_color_secondary} />
          <Text size="s" color="secondary">{t('settings.subscription.loadingPaymentHistory')}</Text>
        </View>
      );
    } else if (purchaseHistory.length === 0) {
      content = (
        <View style={styles.emptyState}>
          <BillingTile icon="receipt" size={40} radius={10} iconSize={16} />
          <Text size="n" weight="medium" color="primary" style={{ marginTop: 6 }}>{t('settings.subscription.noPaymentHistory')}</Text>
          <Text size="s" color="secondary" style={{ textAlign: 'center' }}>{t('settings.subscription.purchasesAppearHere')}</Text>
        </View>
      );
    }

    return (
      <BillingSection
        title={t('settings.subscription.paymentHistory')}
        accessory={historyPagination?.total > 0
          ? <Text size="s" color="secondary">{historyPagination.total}</Text>
          : null}
      >
        {content ? <BillingGroup>{content}</BillingGroup> : (
          <BillingGroup inset={BILLING_ROW_ICON_INSET}>
            {purchaseHistory.map(renderReceiptRow)}
            {historyPagination?.hasMore && (
              <Pressable
                key="load-more"
                accessibilityRole="button"
                onPress={handleLoadMoreHistory}
                disabled={isLoadingMoreHistory}
                style={({ hovered }) => [styles.emptyState, { paddingVertical: 14 }, hovered && { backgroundColor: palette.hover }]}
              >
                {isLoadingMoreHistory
                  ? <ActivityIndicator size="small" color={colors?.text_color_secondary} />
                  : <Text size="n" weight="medium" style={{ color: colors?.text_color_link }}>{t('common.loadMore')}</Text>}
              </Pressable>
            )}
          </BillingGroup>
        )}
      </BillingSection>
    );
  };

  return (
    <View style={styles.container} onLayout={handleContainerLayout}>
      {renderBillingIssue()}
      {renderPlanCard()}
      {renderPaymentMethod()}

      {/* Hidden on iOS: auto top-up charges a card outside Apple IAP. */}
      {hasActiveSubscription && usesHostedBilling() && (
        <BillingGroup>
          <View style={styles.embedded}>
            <AutoTopUpSettings />
          </View>
        </BillingGroup>
      )}

      {renderPlanFeatures()}
      {renderStoreActions()}
      {renderReceipts()}
    </View>
  );
}
